//@flow
import React, {useMemo} from 'react';
import {IconButton, makeStyles} from '@material-ui/core';

import {useFormContext} from 'react-hook-form';
import {
  POPUP_BANNER_EDITOR_ID,
  POPUP_BANNER_FIELDS,
  POPUP_BANNER_CONTENT_ID,
  POPUP_BANNER_LAYOUTS,
  POPUP_BANNER_CLOSE_ID,
  POPUP_LAYOUT_SIZE,
} from '../constants';

import {Close} from '@material-ui/icons';
type StyleProps = {
  backgroundColor?: string,
};

const useClasses = ({backgroundColor}: StyleProps) =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
      overflow: 'auto',
      background: '#F2F2F2',
      borderRadius: '.75rem',
      padding: '1rem',
    },
    editorSection: {
      display: 'flex',
      gap: '.5rem',
      justifyContent: 'center',
      alignItems: 'center',
    },
    container: {
      display: 'flex',
      gap: '1rem',
      backgroundColor: backgroundColor || '#fff',
      position: 'relative',
      overflow: 'hidden',
      minWidth: '300px',
      width: '100%',
      maxWidth: '340px',
    },
    containerVertical: {
      aspectRatio: 75 / 106,
    },
    containerCenter: {
      aspectRatio: 22 / 25,
    },
    content: {
      left: 0,
      bottom: 0,

      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      position: 'absolute',
      zIndex: 2,
      gap: '.5rem',
      pointerEvents: 'none',
    },
    contentVertical: {
      width: '100%',
      height: '45%',
      background: 'white',
    },
    contentCenter: {
      width: '100%',
      height: '100%',
      background: 'transparent',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      margin: 0,
      color: 'inherit',
      lineHeight: '1.1',
    },
    subTitle: {
      fontSize: '.85rem',
      fontWeight: '500',
      margin: 0,
      color: 'inherit',
    },
    button: {
      margin: '0',
      marginTop: '1rem',
      padding: '.75rem 2rem',
      backgroundColor: '#000',
      color: '#fff',
      border: 'none',
      borderRadius: '80px',
      fontSize: '.85rem',
      fontWeight: '600',
      width: 'max-content',
      cursor: 'pointer',
    },
    closePopupButton: {
      position: 'absolute',
      top: '.5rem',
      right: '.5rem',
      color: '#fff',
      padding: '.5rem',
      backgroundColor: 'rgba(0,0,0,.25)',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,.5)',
      },
    },
    belowImage: {
      width: '100%',
      height: 'auto',
      aspectRatio: POPUP_LAYOUT_SIZE[POPUP_BANNER_LAYOUTS.BELOW].aspectRatio,
      position: 'absolute',
      top: 0,
      left: 0,
    },
  }));

export default function PopupBannerEditor() {
  const {watch} = useFormContext();

  const layout = watch(POPUP_BANNER_FIELDS.CONTENT_POSITION);
  const buttonBgColor = watch(POPUP_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR);
  const buttonTextColor = watch(POPUP_BANNER_FIELDS.BUTTON_TEXT_COLOR);
  const buttonText = watch(POPUP_BANNER_FIELDS.BUTTON_TEXT);
  const bannerTextColor = watch(POPUP_BANNER_FIELDS.TEXT_COLOR);
  const bannerBgColor = watch(POPUP_BANNER_FIELDS.BACKGROUND_COLOR);
  const bannerTitle = watch(POPUP_BANNER_FIELDS.TITLE);
  const bannerSubTitle = watch(POPUP_BANNER_FIELDS.SUBTITLE);

  const bannerImage = watch(POPUP_BANNER_FIELDS.IMAGE);

  const classes = useClasses({backgroundColor: bannerBgColor})();

  const contentStyles = {
    color: bannerTextColor,
  };

  const buttonStyles = {
    backgroundColor: buttonBgColor,
    color: buttonTextColor,
  };

  const containerClasses = `${classes.container} ${
    layout === POPUP_BANNER_LAYOUTS.BELOW
      ? classes.containerVertical
      : classes.containerCenter
  }`;

  const contentClasses = `${classes.content} ${
    layout === POPUP_BANNER_LAYOUTS.BELOW
      ? classes.contentVertical
      : classes.contentCenter
  }`;

  const imageUrl = useMemo(() => {
    if (!bannerImage) return;
    return URL.createObjectURL(bannerImage);
  }, [bannerImage]);

  if (!bannerImage)
    return (
      <div className={classes.root}>
        <section className={classes.editorSection}>
          <div className={containerClasses}>
            <IconButton
              className={classes.closePopupButton}
              // id={POPUP_BANNER_CLOSE_ID}
            >
              <Close fontSize="small" />
            </IconButton>
            <div
              className={contentClasses}
              style={contentStyles}
              id={POPUP_BANNER_CONTENT_ID}
            >
              <h3 className={classes.title} id="main_banner_home-title">
                {bannerTitle}
              </h3>
              <h4 className={classes.subTitle} id="main_banner_home-subtitle">
                {bannerSubTitle}
              </h4>
              <button
                className={classes.button}
                style={buttonStyles}
                id="main_banner_home-button"
              >
                {buttonText}
              </button>
            </div>
          </div>
        </section>
      </div>
    );

  return (
    <div className={classes.root}>
      <section className={classes.editorSection}>
        <div id={POPUP_BANNER_EDITOR_ID} className={containerClasses}>
          <img
            src={imageUrl}
            alt=""
            className={
              layout === POPUP_BANNER_LAYOUTS.BELOW ? classes.belowImage : ''
            }
          />
          <IconButton
            className={classes.closePopupButton}
            id={POPUP_BANNER_CLOSE_ID}
          >
            <Close fontSize="small" />
          </IconButton>
          <div
            className={contentClasses}
            style={contentStyles}
            id={POPUP_BANNER_CONTENT_ID}
          >
            <h3 className={classes.title} id="main_banner_home-title">
              {bannerTitle}
            </h3>
            <h4 className={classes.subTitle} id="main_banner_home-subtitle">
              {bannerSubTitle}
            </h4>
            <button
              className={classes.button}
              style={buttonStyles}
              id="main_banner_home-button"
            >
              {buttonText}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
